/**
 * @file Toast 全局轻提示
 * @description 右上角消息栈；fade；zustand 驱动。
 *
 * @whenToUse 操作成功/失败反馈。
 * @whenNotToUse 页内常驻提示（Alert）；需确认（Confirm/Dialog）。
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { create } from 'zustand'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type ToastVariant = 'default' | 'success' | 'danger'

type ToastItem = {
  id: string
  title: string
  description?: string
  variant: ToastVariant
}

type ToastState = {
  items: ToastItem[]
  push: (item: Omit<ToastItem, 'id'> & { id?: string }) => string
  dismiss: (id: string) => void
}

export const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (item) => {
    const id = item.id ?? `toast-${Date.now()}-${Math.random().toString(16).slice(2)}`
    set((state) => ({
      items: [...state.items, { ...item, id, variant: item.variant ?? 'default' }],
    }))
    return id
  },
  dismiss: (id) =>
    set((state) => ({ items: state.items.filter((t) => t.id !== id) })),
}))

/** 命令式 API */
export const toast = {
  show: (title: string, options?: { description?: string; variant?: ToastVariant }) =>
    useToastStore.getState().push({
      title,
      description: options?.description,
      variant: options?.variant ?? 'default',
    }),
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'success' }),
  danger: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, variant: 'danger' }),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
}

const variantClass: Record<ToastVariant, string> = {
  default: 'border-border',
  success: 'border-success/40',
  danger: 'border-danger/40',
}

/**
 * 挂载在应用根部，渲染 Toast 栈
 */
export function ToastViewport() {
  const items = useToastStore((s) => s.items)
  const dismiss = useToastStore((s) => s.dismiss)
  const [ready, setReady] = useState(false)

  useEffect(() => setReady(true), [])

  useEffect(() => {
    if (items.length === 0) return
    const timers = items.map((item) =>
      window.setTimeout(() => dismiss(item.id), 2000),
    )
    return () => timers.forEach((t) => window.clearTimeout(t))
  }, [items, dismiss])

  if (!ready || typeof document === 'undefined') return null

  return createPortal(
    <div className="pointer-events-none fixed top-4 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
      {items.map((item) => (
        <div
          key={item.id}
          className={cn(
            'pointer-events-auto rounded-xl border bg-raised px-3 py-2 text-sm transition-opacity duration-200',
            variantClass[item.variant],
          )}
          role="status"
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-foreground">{item.title}</div>
              {item.description ? (
                <p className="mt-0.5 text-xs text-muted">{item.description}</p>
              ) : null}
            </div>
            <button
              type="button"
              aria-label="关闭"
              className="text-muted transition-opacity duration-200 hover:opacity-90"
              onClick={() => dismiss(item.id)}
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </div>
        </div>
      ))}
    </div>,
    document.body,
  )
}
